import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import PigmentyPopup from "../popups/PigmentyPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiGetDiscountGroupsByType } from "../services/DiscountsService";
import { apiDeletePigment, apiGetAllPigments } from "../services/PigmentsService";

interface Props {
  contextLevel: string;
}

const entityName = 'pigment';
const translationPrefix = 'Pigments';
const translationModel = { prefix: translationPrefix };

const PigmentyPage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [] });
  const [discountGroups, setDiscontGroups] = useState([]);
  const [pigmentPopupData, setPigmentPopupData] = useState({
    visible: false,
    id: undefined,
  });
  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    apiGetAllPigments().then((responseData) => {
      setData({
        rows: responseData.data,
      });

      setLoading(false);
    });
    apiGetDiscountGroupsByType('pigment').then((responseData) => {
      let arr = responseData.data.map((element) => {
        return {
          value: element.groupId,
          label: t(`group${element.groupName}`)//element.groupName
        };
      });
      setDiscontGroups(arr);

      setLoading(false);
    });
  };

  const handleDeleteRow = (row) => {
    apiDeletePigment(row.id)
      .then((responseData) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteSuccess'), row.name), type: 'success' }));
        loadData();

        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.code), type: 'error' }));
      });
  }

  return (
    <TranslationProvider value={translationModel}>
      {pigmentPopupData.visible && (
        <PigmentyPopup
          pigments={data.rows}
          id={pigmentPopupData.id}
          refreshData={() => loadData()}
          handleCancel={() => {
            setPigmentPopupData({ visible: false, id: undefined });
          }}
          discountGroups={discountGroups}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel={t('Label')}
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setPigmentPopupData}
            text={t('CreateText')}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <DataGrid
          loading={loading}
          rows={data.rows}
          showDeleteButton={canDelete}
          showEditButton={canUpdate}
          onDeleteRow={handleDeleteRow}
          handleEditAction={(id: any) => {
            setPigmentPopupData({ visible: true, id: id });
          }}
          cols={[
            { name: t('sortOrder'), dataField: "sortOrder", sortable: true },
            { name: t('Fields.code'), dataField: "code", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.name'), dataField: "name", isLinkToItem: true, sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.price'), fieldType: "money", dataField: "price", sortable: true, filterable: true, filterType: "text" },
            {
              name: t('Fields.discountGroupName'),
              dataField: "discountGroupName",
              sortable: true,
              filterable: true,
              filterType: "text",
              fieldType: "text"
            },
            { name: t('Fields.density'), dataField: "density", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.noteInternal'), dataField: "noteInternal" },
            { name: t('Fields.modifiedOn'), fieldType: "datetime", dataField: "modifiedOn", sortable: true },
            { name: t('Fields.createdOn'), fieldType: "datetime", dataField: "createdOn", sortable: true }
          ]}
        />
      </PageContent>
    </TranslationProvider>
  );
};

export default PigmentyPage;
